/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import React from 'react';
import { Badge, Box, createListCollection, Flex, Link, Select, SimpleGrid, Text, Portal } from '@chakra-ui/react';
import type { DemandSource } from '../../../types';
import { paramsDtoToRows } from './parameter-editor.adapters';
import { templateToLeaves } from './ortb-body-editor.adapters';
import { isRequiredHere } from '@destination-field';

interface TemplateSelectorProps {
  /**
   * **The template artifacts, not a projection of them** (`TM2`, `#1169`).
   *
   * This took `AdServerPreset[]`, whose four content fields could describe a VAST template's query
   * parameters and nothing else — so the summary below reported *Required: 0 · Optional: 0* for an
   * ORTB template carrying 55 body leaves, which is the surface saying the template is empty.
   */
  templates: DemandSource[];
  selectedTemplateId?: string;
  onSelectTemplate: (templateId: string) => void;
  loading?: boolean;
  error?: string | null;
}

export const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  templates,
  selectedTemplateId,
  onSelectTemplate,
  loading,
  error,
}) => {
  const collection = createListCollection({
    items: templates.map((template) => ({ label: template.name ?? String(template.id), value: String(template.id) })),
  });

  const selected = templates.find((template) => String(template.id) === selectedTemplateId);
  /**
   * **Every preset gets a tile** (`#1090`, reported from testing 2026-08-25).
   *
   * This was `presets.slice(0, 6)` — the first six in list order, which is seed order — so the
   * grid showed five VAST exchanges and one *generic* ORTB entry, and **every ORTB SSP we ship
   * (OpenX, AppNexus, PubMatic, Index Exchange) was reachable only by opening the dropdown.**
   * Reported as *"no ORTB presets for any SSP?"*, which is what the surface said.
   *
   * The six was meant to be a curated set: `AdServerPreset.featured` existed for exactly this and
   * **was set by no preset and read by nothing**, so the cut was arbitrary and looked deliberate.
   * Rather than replace one arbitrary rule with another, the rule is removed — fourteen compact
   * tiles at three columns is five rows, and the protocol badge below is what makes the set
   * scannable, which is the thing the operator was actually missing.
   */
  const tiles = templates;

  return (
    <Box>
      <Text fontSize="xs" color="fg.muted" mb={2}>
        Choose a known ad server template to pre-fill everything it carries — method, timeout, headers, request
        parameters and, for OpenRTB, its request body.
      </Text>

      <Select.Root
        size="sm"
        collection={collection}
        value={selectedTemplateId ? [selectedTemplateId] : []}
        onValueChange={(details) => {
          const nextId = details.value[0];
          if (nextId) onSelectTemplate(nextId);
        }}
        disabled={loading || templates.length === 0}
      >
        <Select.Trigger borderColor="border.default">
          <Select.ValueText placeholder={loading ? 'Loading templates…' : '-- Select an ad server --'} />
        </Select.Trigger>
        <Portal>
          <Select.Positioner>
            <Select.Content>
              {collection.items.map((item) => (
                <Select.Item key={item.value} item={item}>
                  {item.label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Positioner>
        </Portal>
      </Select.Root>

      {error && (
        <Text fontSize="xs" color="error.solid" mt={2}>
          {error}
        </Text>
      )}

      {tiles.length > 0 && (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={2} mt={3}>
          {tiles.map((preset) => {
            const isSelected = String(preset.id) === selectedTemplateId;
            return (
              <Box
                key={String(preset.id)}
                border="1px solid"
                borderColor={isSelected ? 'accent.solid' : 'border.default'}
                borderRadius="8px"
                p={2}
                bg={isSelected ? 'accent.subtle' : 'bg.card'}
                cursor="pointer"
                onClick={() => onSelectTemplate(String(preset.id))}
              >
                <Flex align="center" gap={2} mb={1}>
                  <Text fontSize="xs" fontWeight="600" color="fg.title">
                    {preset.name}
                  </Text>
                  {/* Which protocol this exchange is configured for. Absent, an operator looking
                      for an ORTB partner had to select each one to find out. */}
                  <Badge bg="info.tint" color="info.ink" fontSize="2xs">
                    {preset.method}
                  </Badge>
                </Flex>
                <Text fontSize="xs" color="fg.muted">
                  {preset.description || 'Preset configuration'}
                </Text>
              </Box>
            );
          })}
        </SimpleGrid>
      )}

      {/*
        What the template did and did not supply for the endpoint (#955).
        An endpoint shape is populated only where we have a verified example — most vendors have
        none, and without saying so the selector reads as broken ("the suggestion doesn't fill for
        this server") when it is being deliberately silent. A blank is honest; an unexplained blank
        is not.
      */}
      {selected && (
        <Text fontSize="xs" color="fg.muted" mt={2}>
          {selected.endpoint ? (
            <>
              Endpoint pre-filled with {selected.name}&rsquo;s URL shape — replace anything in{' '}
              <Text as="span" fontFamily="mono">
                {'{braces}'}
              </Text>{' '}
              with the values your ad server gave you.
            </>
          ) : (
            <>
              No endpoint suggestion for {selected.name} — we only pre-fill a URL we have verified, because a wrong
              endpoint fails silently. Paste yours above
              {selected.documentationUrl ? (
                <>
                  {' '}
                  or see the{' '}
                  <Link href={selected.documentationUrl} target="_blank" rel="noreferrer" color="accent.solid">
                    {selected.name} docs
                  </Link>
                </>
              ) : null}
              .
            </>
          )}
        </Text>
      )}

      {selected ? <TemplateContents template={selected} /> : null}
    </Box>
  );
};

/**
 * **What this template carries**, read off the artifact (`TM2`, `#1169`).
 *
 * The panel this replaces was headed *Parameter Rules* and counted `AdServerPreset.fields`, which
 * only ever described query parameters. Every ORTB template therefore rendered nothing at all —
 * and the one destination it could describe is the one OpenRTB does not use (10 of 11 ORTB sources
 * on the pre-pilot deployment have **zero** query-parameter rows against 386 authored body leaves).
 * An operator choosing an SSP template was shown an empty panel and told, in effect, that we had
 * nothing for them.
 *
 * Counted rather than enumerated, for the reason `TM5` states about the save side: the operator is
 * owed a statement of what travels, and a statement they can act on is a count and the required
 * names, not the whole document restated in a tooltip.
 */
const TemplateContents: React.FC<{ template: DemandSource }> = ({ template }) => {
  const rows = paramsDtoToRows(template.params);
  const leaves = templateToLeaves(template.ortbBodyTemplate);
  // **Required-ness, not the omission answer** (`X46`). This read `!row.optional`, which is the
  // *wire* answer — omit rather than send `foo=`. The sentence below says "Required:", so it owes
  // the operator the destination's claim, and after the split those are two fields.
  const required = rows.filter((row) => isRequiredHere(row.importance));
  const mapped = rows.filter((row) => row.mode === 'macro');
  const headerCount = Object.keys(template.headers ?? {}).length;

  const lines: string[] = [];
  if (rows.length) lines.push(`${rows.length} request parameter${rows.length === 1 ? '' : 's'}`);
  if (leaves.length) lines.push(`${leaves.length} request-body field${leaves.length === 1 ? '' : 's'}`);
  if (headerCount) lines.push(`${headerCount} header${headerCount === 1 ? '' : 's'}`);
  if (template.responseMapping) lines.push('a response mapping');
  if (template.customPixels?.length) lines.push(`${template.customPixels.length} pixel(s)`);
  if (template.timeout) lines.push(`a ${template.timeout}ms timeout`);

  if (lines.length === 0) return null;

  return (
    <Box mt={3} border="1px solid" borderColor="border.default" borderRadius="8px" p={2} bg="bg.canvas">
      <Text fontSize="xs" fontWeight="600" color="fg.default" mb={1}>
        What this template carries
      </Text>
      <Text fontSize="xs" color="fg.muted">
        {lines.join(' · ')}
      </Text>
      {required.length > 0 && (
        <Text fontSize="xs" color="fg.muted" mt={1}>
          Required:{' '}
          {required
            .slice(0, 8)
            .map((row) => row.name)
            .join(', ')}
          {required.length > 8 ? `, +${required.length - 8} more` : ''}
        </Text>
      )}
      {mapped.length > 0 && (
        <Text fontSize="xs" color="fg.muted" mt={1}>
          {/* Shows the mapping, not just the parameter — the whole point is that a vendor's name
              and ours are two different things, and a bare list of names hides which of the two it
              is naming. */}
          Pre-mapped:{' '}
          {mapped
            .slice(0, 6)
            .map((row) => `${row.name} → ${String(row.value ?? '')}`)
            .join(', ')}
        </Text>
      )}
    </Box>
  );
};
